import React, {
  Children,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  FunctionComponent,
  MouseEventHandler,
} from "react";
import "./index.scss";
import { Typography } from "@material-ui/core";
import { Thumb } from "./EmblaCarouselThumbs";
import useEmblaCarousel from "embla-carousel-react";
import { ContentPageItem } from "../../services/entities/content";

type EmblaCarouselProps = {
  title?: string;
  hasThumbs?: boolean;
  hasDots?: boolean;
  children?: ReactNode;
  largeNavArrows?: boolean;
  hasArrows?: boolean;
  slidesToScroll?: number;
  loop?: boolean;
  variant?: string | null;
  align?: number;
  startIndex?: number;
  item?: ContentPageItem;
  draggable?:boolean;
};

export const EmblaCarousel: FunctionComponent<EmblaCarouselProps> = ({
  title,
  hasThumbs = true,
  hasDots = false,
  children,
  largeNavArrows = false,
  hasArrows = true,
  slidesToScroll = 1,
  loop = false,
  draggable = true,
  variant,
  align = 0.5,
  startIndex = 0,
  item,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mainViewportRef, embla] = useEmblaCarousel({
    skipSnaps: false,
    slidesToScroll: slidesToScroll,
    align: align,
    loop: loop,
    draggable: draggable,
    startIndex: startIndex,
    inViewThreshold: 1,
  });
  const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
    containScroll: "keepSnaps",
    selectedClass: "",
    dragFree: true,
    loop: true,
  });
  // Navigation
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const scrollPrev = useCallback(() => {
    embla && embla.scrollPrev();
  }, [embla]);
  const scrollNext = useCallback(() => {
    embla && embla.scrollNext();
  }, [embla]);
  const scrollTo = useCallback(
    (index) => embla && embla.scrollTo(index),
    [embla]
  );
  const onThumbClick = useCallback(
    (index) => {
      if (!embla || !emblaThumbs) return;
      if (emblaThumbs.clickAllowed()) embla.scrollTo(index);
    },
    [embla, emblaThumbs]
  );
  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
  }, [embla, onSelect]);


  //Navigation

  type PrevButtonProps = {
    enabled?: boolean;
    onClick: MouseEventHandler;
  };
  const PrevButton: FunctionComponent<PrevButtonProps> = ({
    enabled,
    onClick,
  }) => {

    const handleClick = (e: any) => {
      e.stopPropagation(); 
      onClick(e);
    }
    
    return (
    <button
      className="embla__button embla__button--prev"
      onClick={(e) => handleClick(e)}
      disabled={!enabled}
    >
      <svg className="embla__button__svg" viewBox="137.718 -1.001 366.563 644">
        <path d="M428.36 12.5c16.67-16.67 43.76-16.67 60.42 0 16.67 16.67 16.67 43.76 0 60.42L241.7 320c148.25 148.24 230.61 230.6 247.08 247.08 16.67 16.66 16.67 43.75 0 60.42-16.67 16.66-43.76 16.67-60.42 0-27.72-27.71-249.45-249.37-277.16-277.08a42.308 42.308 0 0 1-12.48-30.34c0-11.1 4.1-22.05 12.48-30.42C206.63 234.23 400.64 40.21 428.36 12.5z" />
      </svg>
    </button>
  )};

  type NextButtonProps = {
    enabled?: boolean;
    onClick: MouseEventHandler;
  };
  const NextButton: FunctionComponent<NextButtonProps> = ({
    enabled,
    onClick,
  }) => {

    const handleClick = (e: any) => {
      e.stopPropagation(); 
      onClick(e);
    }

    return (
    <button
      className="embla__button embla__button--next"
      onClick={(e) => handleClick(e)}
      disabled={!enabled}
    >
      <svg className="embla__button__svg" viewBox="0 0 238.003 238.003">
        <path d="M181.776 107.719L78.705 4.648c-6.198-6.198-16.273-6.198-22.47 0s-6.198 16.273 0 22.47l91.883 91.883-91.883 91.883c-6.198 6.198-6.198 16.273 0 22.47s16.273 6.198 22.47 0l103.071-103.039a15.741 15.741 0 0 0 4.64-11.283c0-4.13-1.526-8.199-4.64-11.313z" />
      </svg>
    </button>
  )};

  // Push the first child to the back
  const arrayChildren = Children.toArray(children);
  arrayChildren.push(arrayChildren.shift());

  return (
    <>
      {title ? (
        <div className="inner-title">
          <Typography component="h3" variant="h3">
            {title}
          </Typography>
        </div>
      ) : (
        ""
      )}
      <div
        className={`${
          largeNavArrows ? "large-nav-arrows" : ""
        } carousel-wrapper ${variant && variant}`}
      >
        <div className="embla">
          <div className="embla__viewport" ref={mainViewportRef}>
            <div className="embla__container main-carousel">
              {Children.map(children, (child, index) => (
                <div className={`embla__slide`} key={index}>
                  <div className="embla__slide__inner">{child}</div>
                </div>
              ))}
            </div>
          </div>
          {hasArrows && (
            <div className="navigation">
              <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
              <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
            </div>
          )}
        </div>

        {hasThumbs && (
          <div className="embla embla--thumb">
            <div className="embla__viewport" ref={thumbViewportRef}>
              <div className="embla__container embla__container--thumb thumb-carousel">
                {arrayChildren.map((child, index) => (
                  <Thumb
                    onClick={() =>
                      onThumbClick(
                        index === arrayChildren.length - 1 ? 0 : index + 1
                      )
                    }
                    selected={index === selectedIndex}
                    key={index}
                  >
                    {child}
                  </Thumb>
                ))}
              </div>
            </div>
          </div>
        )}
        {hasDots && arrayChildren.length > 1 && (
          <div className="embla__dots">
            {arrayChildren.map((_, index) => (
              <div
                className={`embla__dot ${
                  index === selectedIndex ? "is-selected" : ""
                }`}
                key={index}
                onClick={() => scrollTo(index)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default EmblaCarousel;
